import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { TimelineLabelI } from "tweenly"
import { paintSequences } from "tweenly"
import { formatTime, parseTime, validateTimeString } from "../helpers/timeline.helpers"
import { setIsPlaying, setTimeToSeek } from "../store/slices/masterTimeline.slice"
import { publishCurrentTime } from "./useCurrentTime"
import { useAppDispatch, useAppSelector, useAppStore } from "./useRedux"
import { scrollTimelineTo } from "./useTimelineViewport"

const MAX_NATURAL_INCREMENT = 0.5
const START_TIME_EPSILON = 0.000001

// Shared SliderTimeProps to ensure consistent behavior and state synchronization
// between CustomLineSlider and TimelineControlTools components.
//
// The playhead time is deliberately absent: it is published to `useCurrentTime` instead, so
// that it does not re-render `TimelineNavbar` (and its whole subtree) on every frame.
export type SliderTimeProps = {
    play: () => void
    pause: (time?: number) => void
    next: () => void
    outro: () => void
    isValid: boolean
    formattedDuration: string
    handleTimeChange: (value: string) => void
    handleChange: (value: number) => void
    goToBegin: () => void
    handleClickTimeStamp: (time: number) => void
}

const useSliderTime = (): SliderTimeProps => {
    const dispatch = useAppDispatch()
    const [duration, setDuration] = useState<number>(0)
    const masterTimeline = useAppSelector((state) => state.masterTimeline.value)
    const frame = useAppSelector((state) => state.timelinePreferences.value.framerate)
    const [isValid, setIsValid] = useState<boolean>(true)
    // `isPlaying` is read on demand, never subscribed. Subscribing here re-rendered
    // TimelineNavbar on every play/pause, and — because it also changed the identity of the
    // handleChange / handleClickTimeStamp callbacks — broke the memo on TimeStamps. Pressing
    // play then re-rendered 500 timestamps and 300 rows (each doing the O(N x A)
    // sortAnimations) before GSAP got a chance to tick, which is why play felt delayed while
    // pause, whose effect is synchronous in GSAP, felt instant.
    const store = useAppStore()
    const getIsPlaying = () => store.getState().masterTimeline.isPlaying
    const timeline = useAppSelector((state) => state.timeline.value)

    const lastTimeRef = useRef<number>(0)
    const isUserSeekingRef = useRef<boolean>(false)
    const skippedLabelTitlesRef = useRef<Set<string>>(new Set())
    const isOutroModeRef = useRef<boolean>(false)

    // assigned further down, once pause()/seek() exist — called straight from GSAP's onUpdate
    const handleCrossedLabelsRef = useRef<(currentTime: number) => void>(() => {})
    const isHandlingLabelsRef = useRef<boolean>(false)

    // Internal helper to update time state without GSAP calls
    const updateTimeState = useCallback(
        (value: number, isUserInitiated = false) => {
            if (!masterTimeline) return 0

            const safeTime = Math.max(0, Math.min(value, masterTimeline.duration()))

            if (isUserInitiated) {
                isUserSeekingRef.current = true
            }

            publishCurrentTime(safeTime)
            lastTimeRef.current = safeTime

            if (isUserInitiated) {
                setTimeout(() => {
                    isUserSeekingRef.current = false
                }, 0)
            }

            return safeTime
        },
        [masterTimeline]
    )

    const seek = useCallback(
        (value: number, isUserInitiated = false) => {
            if (!masterTimeline) return

            const safeTime = updateTimeState(value, isUserInitiated)
            masterTimeline.time(safeTime)
        },
        [masterTimeline, updateTimeState]
    )

    useEffect(() => {
        if (!masterTimeline) return
        setDuration(masterTimeline.duration())
        // the timeline was just rebuilt and paused at its seek position without firing
        // callbacks - make sure the displayed time matches it
        publishCurrentTime(Math.round(masterTimeline.time() / 0.02) * 0.02)

        const updateHandler = () => {
            // Decode-gated image-sequence painting for the canvas preview. Same shared
            // tweenly runtime the export drives from onUpdate -> paintSequences().
            paintSequences()

            //optimization for 50fps more then 50 times in sec return 'Maximum update depth exceeded'
            const currentTime = Math.round(masterTimeline.time() / 0.02) * 0.02
            publishCurrentTime(currentTime)

            handleCrossedLabelsRef.current(currentTime)
        }

        masterTimeline.eventCallback("onUpdate", updateHandler)
        masterTimeline.eventCallback("onComplete", () => {
            const terminalLabels = timeline.labels
                .filter(
                    (lbl) =>
                        Math.abs(lbl.time - masterTimeline.duration()) <= START_TIME_EPSILON &&
                        (lbl.type === "jump" || lbl.type === "pause")
                )
                .sort((a, b) => b.time - a.time)

            for (const label of terminalLabels) {
                if (skippedLabelTitlesRef.current.has(label.title)) {
                    skippedLabelTitlesRef.current.delete(label.title)
                    continue
                }

                if (isOutroModeRef.current) {
                    continue
                }

                if (label.type === "pause") {
                    const safeTime = Math.max(0, Math.min(label.time, masterTimeline.duration()))
                    publishCurrentTime(safeTime)
                    lastTimeRef.current = safeTime
                    masterTimeline.pause(safeTime)
                    dispatch(setIsPlaying(false))
                    return
                }

                if (label.type === "jump") {
                    const jumpToLabel = timeline.labels.find((lbl) => lbl.title === label.jumpto)
                    if (jumpToLabel) {
                        const safeTime = Math.max(
                            0,
                            Math.min(jumpToLabel.time, masterTimeline.duration())
                        )
                        publishCurrentTime(safeTime)
                        lastTimeRef.current = safeTime
                        masterTimeline.time(safeTime)
                        masterTimeline.play()
                        dispatch(setIsPlaying(true))
                        return
                    }
                }
            }

            isOutroModeRef.current = false
            skippedLabelTitlesRef.current.clear()

            // the playhead reached the end of the timeline - pause explicitly, a completed
            // GSAP timeline is not `paused()`, which next()/outro() rely on
            const endTime = masterTimeline.duration()
            publishCurrentTime(endTime)
            lastTimeRef.current = endTime
            masterTimeline.pause(endTime)
            dispatch(setIsPlaying(false))
        })

        return () => {
            masterTimeline.eventCallback("onUpdate", null)
            masterTimeline.eventCallback("onComplete", null)
        }
    }, [masterTimeline, frame, dispatch, timeline.labels])

    const play = useCallback(() => {
        if (!masterTimeline) return

        if (masterTimeline.progress() === 1) {
            isOutroModeRef.current = false
            skippedLabelTitlesRef.current.clear()
            seek(0)
        }

        masterTimeline.play()
        dispatch(setIsPlaying(true))
    }, [masterTimeline, seek, dispatch])

    const pause = useCallback(
        (value?: number, isUserInitiated = false) => {
            if (!masterTimeline) return

            if (value !== undefined) {
                // Update state and get clamped time, then pause (one GSAP call)
                const safeTime = updateTimeState(value, isUserInitiated)
                masterTimeline.pause(safeTime)
            } else {
                // Pause at current position
                masterTimeline.pause()
            }

            dispatch(setIsPlaying(false))
        },
        [masterTimeline, updateTimeState, dispatch]
    )

    const handleChange = useCallback(
        (val: number) => {
            if (getIsPlaying()) return

            const rawTime = (val / 100) * duration
            const frameTime = frame === 1 ? 0.05 : 1 / frame
            const roundedTime = Math.round(rawTime / frameTime) * frameTime

            seek(roundedTime, true)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [duration, frame, seek]
    )

    // the edited string itself now lives in TimelineTimeField, which renders the input
    const handleTimeChange = useCallback(
        (value: string) => {
            if (validateTimeString(value, frame)) {
                const parsed = parseTime(value, frame)
                if (!isNaN(parsed) && parsed <= duration) {
                    setIsValid(true)
                    seek(parsed, true)
                    dispatch(setTimeToSeek(parsed))
                } else {
                    setIsValid(false)
                }
            } else {
                setIsValid(false)
            }
        },
        [frame, duration, seek, dispatch]
    )

    const handleClickTimeStamp = useCallback(
        (value: number) => {
            if (getIsPlaying()) {
                pause(value, true)
            } else {
                seek(value, true)
            }
            dispatch(setTimeToSeek(value))
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pause, seek, dispatch]
    )

    const goToBegin = useCallback(() => {
        isOutroModeRef.current = false
        skippedLabelTitlesRef.current.clear()

        seek(0, true)
        pause(0, true)

        scrollTimelineTo(0)
    }, [seek, pause])

    const next = useCallback(() => {
        if (!masterTimeline || !timeline?.labels?.length) return

        const currentTime = masterTimeline.time()
        const nextLabel = [...timeline.labels]
            .filter(
                (lbl) => lbl.time > currentTime && (lbl.type === "jump" || lbl.type === "pause")
            )
            .sort((a, b) => a.time - b.time)[0]
        const currentLabel = timeline.labels.find(
            (lbl) =>
                Math.abs(lbl.time - currentTime) <= START_TIME_EPSILON &&
                (lbl.type === "jump" || lbl.type === "pause")
        )

        if (masterTimeline.paused()) {
            play()
            return
        }

        if (currentLabel) {
            skippedLabelTitlesRef.current.add(currentLabel.title)
            play()
            return
        }

        if (!nextLabel) return

        skippedLabelTitlesRef.current.add(nextLabel.title)
        play()
    }, [masterTimeline, timeline, play])

    const outro = useCallback(() => {
        if (!masterTimeline) return

        isOutroModeRef.current = true

        if (masterTimeline.paused()) {
            masterTimeline.play()
            dispatch(setIsPlaying(true))
        }
    }, [masterTimeline, dispatch])

    const formattedDuration = useMemo(() => formatTime(duration, frame), [duration, frame])

    /**
     * Handles the timeline labels crossed since the previous playhead position.
     *
     * Used to be an effect keyed on the `time` state, which is why the playhead time had to
     * live in React state at all — and why `labels.filter().sort()` allocated on every single
     * rendered frame. It now runs straight from GSAP's `onUpdate`, so labels are handled on
     * the exact frame they are crossed instead of one render later.
     *
     * Reassigned on every render so the closure always sees the current `timeline`, `pause`
     * and `seek` — `onUpdate` is registered once and would otherwise keep the values from the
     * render that registered it. `isPlaying` is read straight from the store, so it is correct
     * even between renders.
     */
    handleCrossedLabelsRef.current = (currentTime: number) => {
        if (!getIsPlaying() || !timeline?.labels) return

        //skip label detection during user-initiated seek operations
        if (isUserSeekingRef.current) {
            return
        }

        //pause()/seek() drive GSAP, which fires onUpdate again - do not recurse into ourselves
        if (isHandlingLabelsRef.current) {
            return
        }

        const lastTime = lastTimeRef.current

        //skip wide time ranges that indicate seek operations (not natural playback)
        const timeDiff = Math.abs(currentTime - lastTime)

        if (timeDiff > MAX_NATURAL_INCREMENT) {
            lastTimeRef.current = currentTime
            return
        }

        //only allocate when a label is really crossed - this runs on every animation frame
        let labels: TimelineLabelI[] | null = null
        for (const label of timeline.labels) {
            //[NOTE]: pri tweenly-lib je pridana tato podmienka ale v editore znefuncnuje tato podmienka moznost spustit
            // timeline pokial je label pause time = 0
            // || (lbl.time === 0 && lastTime === 0 && currentTime > 0)
            if (label.time > lastTime && label.time <= currentTime) {
                if (!labels) labels = []
                labels.push(label)
            }
        }

        if (labels) {
            labels.sort((a, b) => b.time - a.time)

            isHandlingLabelsRef.current = true
            try {
                labels.forEach((label) => {
                    if (skippedLabelTitlesRef.current.has(label.title)) {
                        skippedLabelTitlesRef.current.delete(label.title)
                        return
                    }

                    if (
                        isOutroModeRef.current &&
                        (label.type === "pause" || label.type === "jump")
                    ) {
                        return
                    }

                    if (label.type === "pause") {
                        pause(label.time)
                    } else if (label.type === "jump") {
                        const jumpToLabel = timeline.labels.find(
                            (lbl: TimelineLabelI) => lbl.title === label.jumpto
                        )
                        if (jumpToLabel) {
                            seek(jumpToLabel.time)
                        }
                    }
                })
            } finally {
                isHandlingLabelsRef.current = false
            }
        }

        lastTimeRef.current = currentTime
    }

    return {
        play,
        pause,
        next,
        outro,
        isValid,
        formattedDuration,
        handleTimeChange,
        handleChange,
        goToBegin,
        handleClickTimeStamp,
    }
}

export default useSliderTime
